import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import api from "../../../../chat/api";
import { FilterChevronIcon } from "../Proposal Section View/svgIcons";
import CommentTab from "./CommentTab";
import ReviewStatusTab from "./ReviewStatusTab";

const TeamMembersModal = ({
  isOpen,
  onClose,
  proposalData,
  rightViewTab,
  setRightViewTab,
  filterType,
  setFilterType,
  isDropdownOpen,
  setIsDropdownOpen,
  expandedMemberIds,
  toggleMemberCard,
  getInitials,
  orgId,
  folderId,
  onRefresh,
  userPermission,
}) => {
  const [commentInputs, setCommentInputs] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [successId, setSuccessId] = useState(null);
  const [resolvingIds, setResolvingIds] = useState({});
  const [resolveSuccessIds, setResolveSuccessIds] = useState({});
  const [currentUserEmail, setCurrentUserEmail] = useState("");

  useEffect(() => {
    const storedEmail = localStorage.getItem("email");
    if (storedEmail) {
      setCurrentUserEmail(storedEmail);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset local comment/resolve state when the modal closes
  useEffect(() => {
    if (!isOpen) {
      setCommentInputs({});
      setLoadingId(null);
      setSuccessId(null);
      setResolvingIds({});
      setResolveSuccessIds({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canResolve = userPermission !== "reviewer" && userPermission !== "editor";

  const handleCommentChange = (uniqueId, text) => {
    setCommentInputs((prev) => ({ ...prev, [uniqueId]: text }));
  };

  const handleAddComment = async (uniqueId, sectionId, userId) => {
    const commentText = commentInputs[uniqueId];
    if (!commentText || !commentText.trim()) return;
    if (!orgId || !folderId || !sectionId) {
      toast.error("Missing required project information");
      return;
    }

    setLoadingId(uniqueId);

    try {
      const payload = {
        org_id: parseInt(orgId),
        section_id: sectionId,
        user_id: userId,
        comment: commentText,
      };

      await api.post(`/api/v1/rfp/folders/${folderId}/section-view/add-comment/`, payload);

      setLoadingId(null);
      setSuccessId(uniqueId);
      setCommentInputs((prev) => ({ ...prev, [uniqueId]: "" }));

      setTimeout(() => {
        setSuccessId(null);
        if (onRefresh) onRefresh();
      }, 2000);
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
      setLoadingId(null);
    }
  };

  const handleResolveToggle = async (userId, currentStatus, sectionId, uniqueId) => {
    if (!orgId || !folderId || !sectionId) {
      toast.error("Missing required project information");
      return;
    }

    const newStatus = !currentStatus;
    setResolvingIds((prev) => ({ ...prev, [uniqueId]: true }));

    try {
      const payload = {
        org_id: parseInt(orgId),
        section_id: sectionId,
        user_id: userId,
        resolved: newStatus,
      };

      await api.post(`/api/v1/rfp/folders/${folderId}/section-view/resolve-comment/`, payload);

      setResolvingIds((prev) => ({ ...prev, [uniqueId]: false }));

      if (newStatus === true) {
        setResolveSuccessIds((prev) => ({ ...prev, [uniqueId]: true }));
        if (onRefresh) await onRefresh();
        setTimeout(() => {
          setResolveSuccessIds((prev) => ({ ...prev, [uniqueId]: false }));
        }, 2000);
      } else {
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error("Error resolving comment:", error);
      toast.error("Failed to update resolution status");
      setResolvingIds((prev) => ({ ...prev, [uniqueId]: false }));
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end bg-black bg-opacity-20 backdrop-blur-sm transition-opacity duration-300">
      <div className="absolute inset-0" onClick={onClose}></div>

      <div 
        className="w-[45vw] h-full bg-white font-outfit shadow-2xl flex flex-col relative transform transition-transform duration-300 ease-in-out translate-x-0"
        onClick={(e) => e.stopPropagation()} 
      >
        <div className="px-4 py-6 pb-0 flex-shrink-0">
          <div className="flex items-center justify-between mb-4 relative z-50">
            <h2 className="text-[#18101C] font-outfit text-lg font-semibold pr-4">
              Project Team Overview
            </h2>

            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 text-[#374151] font-outfit text-sm font-medium bg-[#F9FAFB] border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#C95EBE] focus:border-transparent"
                >
                  <span>{filterType}</span>
                  <FilterChevronIcon
                    className={`transform transition-transform duration-200 ${
                      isDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsDropdownOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-36 bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-100 z-20 p-2 flex flex-col gap-1 animate-in fade-in zoom-in-95 duration-100">
                      {["All", "Editor", "Reviewer"].map((option) => (
                        <button
                          key={option}
                          onClick={() => {
                            setFilterType(option);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm font-outfit transition-all ${
                            filterType === option
                              ? "bg-white border border-gray-200 font-medium text-[#18101C] shadow-sm"
                              : "text-[#6B7280] hover:bg-gray-50 hover:text-[#374151] border border-transparent"
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex items-center border-b border-gray-200 mb-4">
            <button
              onClick={() => setRightViewTab("comments")}
              className={`flex-1 pb-1 text-[15px] font-medium text-center transition-colors relative ${
                rightViewTab === "comments" ? "text-[#C95EBE]" : "text-[#6B7280] hover:text-[#374151]"
              }`}
            >
              Comments
              {rightViewTab === "comments" && <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#C95EBE]" />}
            </button>
            <button
              onClick={() => setRightViewTab("review-status")}
              className={`flex-1 pb-1 text-[15px] font-medium text-center transition-colors relative ${
                rightViewTab === "review-status" ? "text-[#C95EBE]" : "text-[#6B7280] hover:text-[#374151]"
              }`}
            >
              Review Status
              {rightViewTab === "review-status" && <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#C95EBE]" />}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-6 custom-scrollbar">
          {rightViewTab === "comments" && (
            <CommentTab
              proposalData={proposalData}
              filterType={filterType}
              expandedMemberIds={expandedMemberIds}
              toggleMemberCard={toggleMemberCard}
              getInitials={getInitials}
              commentInputs={commentInputs}
              loadingId={loadingId}
              successId={successId}
              resolvingIds={resolvingIds}
              resolveSuccessIds={resolveSuccessIds}
              canResolve={canResolve}
              handleCommentChange={handleCommentChange}
              handleAddComment={handleAddComment}
              handleResolveToggle={handleResolveToggle}
            />
          )}

          {rightViewTab === "review-status" && (
            <ReviewStatusTab
              isOpen={isOpen}
              proposalData={proposalData}
              filterType={filterType}
              expandedMemberIds={expandedMemberIds}
              toggleMemberCard={toggleMemberCard}
              getInitials={getInitials}
              orgId={orgId}
              folderId={folderId}
              onRefresh={onRefresh}
              userPermission={userPermission}
              currentUserEmail={currentUserEmail}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamMembersModal;
